import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Switch,
  useDisclosure,
} from '@chakra-ui/react';
import { atom, useAtom } from 'jotai';
import { useState } from 'react';

import DevToolWrapper from '../../../components/devTools/DevToolWrapper';
import { isProd } from '../../../config';
import {
  TemporaryData,
  useInsertDevAffiliateFromTempInfoMutation,
  useSendDevAffiliateToThunderMutation,
} from '../../../gql/generated/graphql';
import LoanAppDevToolbar from '../../LoanApp/components/LoanAppDevToolbar';

export const devIgnoreExistingDealOverride = atom(false);
export const devIgnoreExistingVinOverride = atom(false);

const generateRandomExternalId = () => Math.random().toString(36).substring(2, 15);

const AffiliateDevToolbar = ({
  affiliateTempInfoData,
}: {
  affiliateTempInfoData?: TemporaryData;
}) => {
  const [ignoreExistingDeal, setIgnoreExistingDeal] = useAtom(devIgnoreExistingDealOverride);
  const [ignoreExistingVin, setIgnoreExistingVin] = useAtom(devIgnoreExistingVinOverride);
  const [externalId, setExternalId] = useState('');
  const [tempInfoId, setTempInfoId] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [insertAffiliate, { loading, data: insertAffiliateData }] =
    useInsertDevAffiliateFromTempInfoMutation();

  const [sendAffiliateToThunder, { loading: sendToThunderLoading }] =
    useSendDevAffiliateToThunderMutation();

  if (isProd) return null;

  const handleSubmit = async () => {
    if (tempInfoId) {
      const finalExternalId = externalId || generateRandomExternalId();
      await insertAffiliate({ variables: { externalId: finalExternalId, tempInfoId } });
      onClose();
    }
  };

  const handleSendToThunder = async () => {
    const affiliateExternalId = affiliateTempInfoData?.affiliate_external_id;

    if (affiliateExternalId) {
      await sendAffiliateToThunder({ variables: { externalId: affiliateExternalId } });
    }
  };

  return (
    <DevToolWrapper>
      {affiliateTempInfoData && (
        <>
          <Switch
            isChecked={ignoreExistingDeal}
            onChange={(e) => setIgnoreExistingDeal(e.target.checked)}
          >
            Ignore Existing Deal
          </Switch>
          <Switch
            isChecked={ignoreExistingVin}
            onChange={(e) => setIgnoreExistingVin(e.target.checked)}
          >
            Ignore Existing VIN
          </Switch>
          <LoanAppDevToolbar includeTitle={false} />
          <Button onClick={handleSendToThunder} isLoading={sendToThunderLoading}>
            Send to Thunder
          </Button>
        </>
      )}
      {insertAffiliateData && (
        <>
          Affiliate inserted:
          <Link href={insertAffiliateData.insertDevAffiliateFromTempInfo ?? ''}>
            {insertAffiliateData.insertDevAffiliateFromTempInfo}
          </Link>
        </>
      )}
      <Popover isOpen={isOpen} onClose={onClose}>
        <PopoverTrigger>
          <Button onClick={onOpen}>Insert Affiliate</Button>
        </PopoverTrigger>
        <PopoverContent>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader>Insert Affiliate</PopoverHeader>
          <PopoverBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Desired External ID (Optional)</FormLabel>
                <Input
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                  placeholder="Leave blank for random ID"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Temp Info ID</FormLabel>
                <Input
                  value={tempInfoId}
                  onChange={(e) => setTempInfoId(e.target.value)}
                  placeholder="Enter temp info ID"
                />
              </FormControl>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={loading}
                isDisabled={!tempInfoId}
              >
                Submit
              </Button>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </DevToolWrapper>
  );
};

export default AffiliateDevToolbar;
